import React, { useState, useEffect } from 'react';
import { Send, UserPlus, Phone, MessageSquare, CheckCircle, AlertCircle, Loader, Users, Search, Sparkles, Filter, XCircle, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { estimateSms, explainSmsFailure } from '../../utils/smsEstimate';
import SmsHistory from './SmsHistory';

interface Recipient {
  id: string;
  name: string;
  phone: string;
  type: 'student' | 'lead' | 'visa_tracking' | 'visa_appointment';
  marketing_consent?: boolean;
  registration_year?: string;
}

interface MessageTemplate {
  id: string;
  title: string;
  content: string;
  category: string;
  created_by?: string;
  branch_id?: string;
  department?: string;
}

type MessagePurpose = 'commercial' | 'informational';

const SmsSender: React.FC = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState<'send' | 'history'>('send');
  const [students, setStudents] = useState<any[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<Recipient[]>([]);
  const [manualPhones, setManualPhones] = useState<string>('');
  const [message, setMessage] = useState('');
  const [messagePurpose, setMessagePurpose] = useState<MessagePurpose>('commercial');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [checkingBalance, setCheckingBalance] = useState(false);
  const [apiBalance, setApiBalance] = useState<{ amount: number; sms: number | null; sender: string } | null>(null);
  const [balanceError, setBalanceError] = useState('');
  const [sendFailure, setSendFailure] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [successCount, setSuccessCount] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ title: '', content: '' });
  const [filterType, setFilterType] = useState<'all' | 'student' | 'lead' | 'visa_tracking' | 'visa_appointment' | 'consent'>('all');
  const [selectedRegistrationYears, setSelectedRegistrationYears] = useState<string[]>([]);

  useEffect(() => {
    fetchStudents();
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const isVisa = (user?.unitType as string) === 'visa' || (user?.unitType as string) === 'visa_consultant';
      const isTestPrep = (user?.unitType as string) === 'testprep';

      let department = 'sales';
      if (isVisa) department = 'visa';
      if (isTestPrep) department = 'testprep';

      let query = supabase
        .from('sms_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (department) {
        query = query.eq('department', department);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Şablonlar yüklenirken hata:', error);
    }
  };

  const handleAddTemplate = async () => {
    if (!newTemplate.title || !newTemplate.content) {
      alert('Lütfen başlık ve içerik giriniz.');
      return;
    }

    try {
      const isVisa = (user?.unitType as string) === 'visa' || (user?.unitType as string) === 'visa_consultant';
      const isTestPrep = (user?.unitType as string) === 'testprep';

      let department = 'sales';
      if (isVisa) department = 'visa';
      if (isTestPrep) department = 'testprep';

      const { error } = await supabase
        .from('sms_templates')
        .insert([{
          title: newTemplate.title,
          content: newTemplate.content,
          department: department,
          branch_id: user?.branchId,
          created_by: user?.id
        }]);

      if (error) throw error;

      setShowAddTemplate(false);
      setNewTemplate({ title: '', content: '' });
      fetchTemplates();
    } catch (error) {
      console.error('Şablon eklenirken hata:', error);
      alert('Şablon eklenemedi.');
    }
  };

  const handleDeleteTemplate = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!await window.appConfirm('Bu şablonu silmek istediğinize emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('sms_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchTemplates();
    } catch (error) {
      console.error('Şablon silinirken hata:', error);
    }
  };

  // Helper helper to fetch all records with batching
  const fetchAllRecords = async (
    tableName: string,
    selectQuery: string,
    additionalFilters?: (query: any) => any
  ) => {
    try {
      // 1. Get total count first
      let countQuery = supabase
        .from(tableName)
        .select('*', { count: 'exact', head: true });

      if (additionalFilters) {
        countQuery = additionalFilters(countQuery);
      }

      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      const total = count || 0;
      if (total === 0) return [];

      // 2. Fetch in batches of 1000
      const PAGE_SIZE = 1000;
      const totalPages = Math.ceil(total / PAGE_SIZE);
      const promises = [];

      for (let i = 0; i < totalPages; i++) {
        let query = supabase
          .from(tableName)
          .select(selectQuery)
          .range(i * PAGE_SIZE, (i + 1) * PAGE_SIZE - 1);

        // We must apply the same order to ensure consistent paging usually, 
        // but since we are fetching ALL, strict global ordering across chunks 
        // isn't strictly necessary for data integrity if snapshots don't change fast.
        // However, standard practice is to order by ID or Created At.
        // Let's assume ID or Created At availability? 
        // For simplicity and to match existing behavior, we might not strictly need ordering 
        // IF we just grab ranges, but range() works on the query result set. 
        // Without 'order', postgres return order is undefined.
        // Let's add a default order if possible, or trust Supabase/Postgres stability.
        // The original queries had .order('first_name'), which is fine.

        if (additionalFilters) {
          query = additionalFilters(query);
        }

        // Add ordering to ensure stable pagination
        // Check if table has first_name, most do.
        if (tableName === 'visa_appointments') {
          query = query.order('full_name');
        } else {
          query = query.order('first_name', { ascending: true });
          // Some tables might have null first_name, so maybe id fallback?
          // Actually, adding .order('id') is safer for pagination stability.
          query = query.order('id', { ascending: true });
        }

        promises.push(query);
      }

      const results = await Promise.all(promises);

      let allData: any[] = [];
      for (const res of results) {
        if (res.error) throw res.error;
        if (res.data) allData = [...allData, ...res.data];
      }

      return allData;

    } catch (error) {
      console.error(`Error fetching ${tableName}:`, error);
      throw error;
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const isVisaDept = (user?.unitType as string) === 'visa' || (user?.unitType as string) === 'visa_consultant';
      const isTestPrep = (user?.unitType as string) === 'testprep';
      const branchId = user?.branchId;

      // Define filter appliers
      const branchFilter = (q: any) => branchId ? q.eq('branch_id', branchId) : q;
      const submissionFilter = (q: any) => q.is('converted_lead_id', null);

      if (isTestPrep) {
        const testPrepPromise = fetchAllRecords(
          'testprep_students',
          'id, first_name, last_name, phone'
        );

        const results = await Promise.all([testPrepPromise]);

        const testPrepData = (results[0] || []).map((s: any) => ({
          ...s,
          type: 'student' as const
        }));

        // Sort in memory
        testPrepData.sort((a, b) => {
          const nameA = a.first_name + (a.last_name ? ' ' + a.last_name : '');
          const nameB = b.first_name + (b.last_name ? ' ' + b.last_name : '');
          return nameA.localeCompare(nameB, 'tr');
        });

        setStudents(testPrepData);
        setLoading(false);
        return;
      }

      // Queries
      const studentsPromise = fetchAllRecords(
        'students',
        'id, first_name, last_name, phone, branch_id',
        branchFilter
      );

      const leadsPromise = fetchAllRecords(
        'leads',
        'id, first_name, last_name, phone, branch_id, registration_year',
        branchFilter
      );

      const submissionsPromise = fetchAllRecords(
        'website_form_submissions',
        'id, first_name, last_name, phone, marketing_consent, converted_lead_id',
        submissionFilter
      );

      const promises: Promise<any[]>[] = [studentsPromise, leadsPromise, submissionsPromise];

      if (isVisaDept) {
        const visaTrackingPromise = fetchAllRecords(
          'visa_tracking',
          'id, first_name, last_name, phone'
          // No branch filter in original code for visa stuff
        );

        const visaAppointmentsPromise = fetchAllRecords(
          'visa_appointments',
          'id, full_name, phone'
          // No branch filter in original code for visa stuff
        );

        promises.push(visaTrackingPromise);
        promises.push(visaAppointmentsPromise);
      }

      const results = await Promise.all(promises);

      const studentsData = (results[0] || []).map((s: any) => ({ ...s, type: 'student' as const }));
      const leadsData = (results[1] || []).map((l: any) => ({ ...l, type: 'lead' as const }));
      const submissionsData = (results[2] || []).map((s: any) => ({ ...s, type: 'lead' as const }));

      let combined = [...studentsData, ...leadsData, ...submissionsData];

      if (isVisaDept) {
        const visaTrackingData = (results[3] || []).map((v: any) => ({
          id: v.id,
          first_name: v.first_name,
          last_name: v.last_name,
          phone: v.phone,
          type: 'visa_tracking' as const
        }));

        const visaAppointmentsData = (results[4] || []).map((v: any) => ({
          id: v.id,
          first_name: v.full_name, // Using full_name as first_name for sorting/display simplicity
          last_name: '',
          phone: v.phone,
          type: 'visa_appointment' as const
        }));

        combined = [...combined, ...visaTrackingData, ...visaAppointmentsData];
      }

      // Sort in memory
      combined.sort((a, b) => {
        const nameA = a.first_name + (a.last_name ? ' ' + a.last_name : '');
        const nameB = b.first_name + (b.last_name ? ' ' + b.last_name : '');
        return nameA.localeCompare(nameB, 'tr');
      });

      setStudents(combined);
    } catch (error) {
      console.error('Veriler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStudent = (student: any) => {
    const recipient: Recipient = {
      id: student.id,
      name: `${student.first_name} ${student.last_name}`.trim(),
      phone: student.phone,
      type: student.type,
      marketing_consent: student.marketing_consent,
      registration_year: student.registration_year
    };

    setSelectedStudents(prev => {
      const exists = prev.find(s => s.id === student.id && s.type === student.type);
      if (exists) {
        return prev.filter(s => !(s.id === student.id && s.type === student.type));
      } else {
        return [...prev, recipient];
      }
    });
  };

  const removeStudent = (studentId: string, studentType: Recipient['type']) => {
    setSelectedStudents(prev => prev.filter(s => !(s.id === studentId && s.type === studentType)));
  };

  const parseManualPhones = (): string[] => {
    if (!manualPhones.trim()) return [];

    const phones = manualPhones
      .split(/[,;\n]/)
      .map(p => p.trim())
      .filter(p => p.length > 0)
      .map(p => p.replace(/\D/g, ''));

    return [...new Set(phones)];
  };

  const getAllPhones = (): string[] => {
    // Helper to process a single phone string that might contain multiple numbers
    const processPhoneString = (input: string): string[] => {
      if (!input) return [];
      // Split by common delimiters: comma, semicolon, newline, forward slash, or even space if it looks like a separator
      // We also handle cases where numbers might be glued together if they are exactly 10-15 digits long? 
      // Safest is to split by non-digit blocks first.

      // Strategy: 
      // 1. replace all common separators with a unique separator (e.g. |)
      // 2. split by |
      // 3. clean each part

      const parts = input.replace(/[;,/\n\r\t]/g, '|').split('|');

      const results: string[] = [];

      parts.forEach(p => {
        const cleaned = p.replace(/\D/g, '');

        if (cleaned.length >= 10 && cleaned.length <= 15) {
          results.push(cleaned);
        } else if (cleaned.length > 15) {
          // Try to extract Turkish mobile number patterns: 05xxxxxxxxx (11) or 5xxxxxxxxx (10)
          const matches = cleaned.match(/(0?5\d{9})/g);
          if (matches) {
            results.push(...matches);
          }
        }
      });

      return results;
    };

    const studentPhones = selectedStudents
      .flatMap(s => processPhoneString(s.phone || ''));

    const manual = parseManualPhones();

    return [...new Set([...studentPhones, ...manual])];
  };

  const validatePhoneNumber = (phone: string): boolean => {
    let cleaned = phone.replace(/\D/g, '');

    if (cleaned.startsWith('90')) {
      cleaned = cleaned.substring(2);
    } else if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }

    return /^5\d{9}$/.test(cleaned);
  };

  const calculateSmsCount = (text: string) => estimateSms(text).parts;

  const checkBalance = async () => {
    setCheckingBalance(true);
    setBalanceError('');
    setApiBalance(null);
    try {
      const { data, error } = await supabase.functions.invoke('send-sms', { body: { action: 'get_balance' } });
      if (error) throw new Error('Canlı SMS bakiyesi alınamadı. Oturumunuzu ve servis bağlantısını kontrol edin.');
      if (!data?.success || !data?.balance) throw new Error(data?.error || 'Canlı SMS bakiyesi alınamadı.');
      const balance = { ...data.balance, sender: data.sender };
      setApiBalance(balance);
      return balance;
    } catch (error) {
      setBalanceError(error instanceof Error ? error.message : 'Bakiye sorgulanamadı.');
      return null;
    } finally { setCheckingBalance(false); }
  };

  const handleSendSMS = async () => {
    if (sending) return;
    setSendFailure('');
    const phones = getAllPhones();

    if (phones.length === 0) {
      if (selectedStudents.length > 0) {
        alert('Seçilen kayıtlar için geçerli telefon numarası bulunamadı. Lütfen numaraları kontrol edin.');
      } else {
        alert('Lütfen en az bir alıcı seçin veya telefon numarası girin.');
      }
      return;
    }

    if (!message.trim()) {
      alert('Lütfen bir mesaj yazın.');
      return;
    }

    const calculatedCount = calculateSmsCount(message);
    if (calculatedCount > 1) {
      const confirm = await window.appConfirm(
        `Mesajınız ${message.length} karakter; kişi başına yaklaşık ${calculatedCount} SMS, ${phones.length} alıcı için yaklaşık ${(phones.length * calculatedCount).toLocaleString('tr-TR')} SMS kredisi kullanılır. Gerçek tüketim hesabınızın dil moduna bağlıdır. Devam etmek istiyor musunuz?`
      );
      if (!confirm) return;
    }

    const invalidPhones = phones.filter(p => !validatePhoneNumber(p));
    if (invalidPhones.length > 0) {
      alert(`Geçersiz telefon numaraları: ${invalidPhones.join(', ')}`);
      return;
    }

    const confirmSend = await window.appConfirm(
      `${phones.length} kişiye SMS gönderilecek. Devam etmek istiyor musunuz?`
    );

    if (!confirmSend) return;

    setSending(true);
    setSuccessCount(0);

    try {
      const BATCH_SIZE = 50;
      const chunks = [];
      for (let i = 0; i < phones.length; i += BATCH_SIZE) {
        chunks.push(phones.slice(i, i + BATCH_SIZE));
      }

      console.log(`${chunks.length} paket halinde toplam ${phones.length} SMS gönderiliyor...`);

      let sentCount = 0;
      let attemptedCount = 0;
      let hasError = false;
      const sendErrors: string[] = [];

      for (let i = 0; i < chunks.length; i++) {
        const currentBatch = chunks[i];
        attemptedCount += currentBatch.length;
        console.log(`Paket ${i + 1}/${chunks.length} gönderiliyor (${currentBatch.length} numara)...`);

        const { data, error } = await supabase.functions.invoke('send-sms', {
          body: {
            phones: currentBatch,
            message,
            messagePurpose,
            userId: user?.id,
            branchId: user?.branchId
          }
        });

        if (error) {
          console.error(`Paket ${i + 1} hatası:`, error);
          hasError = true;
          alert(`Bir hata oluştu (Paket ${i + 1}): ${error.message || 'Bilinmeyen hata'}`);
          break;
        }

        const batchSuccessCount = Number(data?.summary?.success ?? 0);
        const batchFailedCount = Number(data?.summary?.failed ?? currentBatch.length);
        if (data?.logWarning) setSendFailure(previous => [previous, data.logWarning].filter(Boolean).join(' '));

        sentCount += batchSuccessCount;
        setSuccessCount(sentCount);

        if (!data?.success || batchFailedCount > 0) {
          const providerBalanceError = Array.isArray(data?.results) && data.results.some((result: { error?: string }) => /^402\s*:/.test(result.error || ''));
          if (providerBalanceError) {
            const balance = await checkBalance();
            const explanation = explainSmsFailure('402', balance);
            setSendFailure(explanation);
            sendErrors.push(explanation);
          }
          const resultErrors = Array.isArray(data?.results)
            ? data.results
              .filter((result: any) => !result.success)
              .map((result: any) => `${result.phone}: ${result.error || 'Bilinmeyen hata'}`)
            : [];

          console.error(`Paket ${i + 1} API hatası:`, data);
          hasError = true;
          if (!providerBalanceError) sendErrors.push(...(resultErrors.length > 0 ? resultErrors : [data?.error || 'Bilinmeyen hata']));

          // Sistemsel bir hata tüm paketi etkilediyse sonraki paketleri göndermeyi denemeyin.
          if (batchSuccessCount === 0) break;
        }
      }

      if (!hasError) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 5000);
        setSelectedStudents([]);
        setManualPhones('');
        setMessage('');
      } else {
        alert(
          `${sentCount} alıcının SMS siparişi kabul edildi. ${attemptedCount - sentCount} alıcı için başarılı gönderim doğrulanamadı. ${phones.length - attemptedCount} alıcıya gönderim denenmedi.\n\n` +
          sendErrors.slice(0, 10).join('\n')
        );
      }

    } catch (error: any) {
      console.error('SMS gönderim hatası:', error);
      alert(`SMS gönderilemedi: ${error.message || 'Bilinmeyen hata'}`);
    } finally {
      setSending(false);
    }
  };

  const toRecipient = (student: any): Recipient => ({
    id: student.id,
    name: `${student.first_name} ${student.last_name}`.trim(),
    phone: student.phone,
    type: student.type,
    marketing_consent: student.marketing_consent,
    registration_year: student.registration_year
  });

  const selectAll = () => {
    const recipients = filteredStudents.map(toRecipient);
    setSelectedStudents(prev => {
      const next = [...prev];
      recipients.forEach(recipient => {
        if (!next.some(item => item.id === recipient.id && item.type === recipient.type)) {
          next.push(recipient);
        }
      });
      return next;
    });
  };

  const removeFilteredSelection = () => {
    setSelectedStudents(prev =>
      prev.filter(selected =>
        !filteredStudents.some(student => student.id === selected.id && student.type === selected.type)
      )
    );
  };

  const clearAll = () => {
    setSelectedStudents([]);
  };

  const toggleRegistrationYear = (year: string) => {
    setSelectedRegistrationYears(prev =>
      prev.includes(year) ? prev.filter(item => item !== year) : [...prev, year]
    );
  };

  const useTemplate = (template: MessageTemplate) => {
    setMessage(template.content);
    setShowTemplates(false);
  };

  const filteredStudents = students.filter(student => {
    const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
    const phone = student.phone || '';

    // Check for valid Turkish mobile number format (05xxxxxxxxx)
    // We strictly want 11 digits starting with 05.
    const cleanPhone = phone.replace(/\D/g, '');
    const isValidTurkishMobile = /^05\d{9}$/.test(cleanPhone);

    // If phone has multiple numbers, check if at least one is valid
    // The student.phone might be "05321234567/0533..."
    // Let's use our helper logic implicitly or just quick check.
    // For "Showing" in list, we usually want to show if they HAVE a valid phone.
    // But user asked: "sadece türkiye telefon numarası kayıtlı olanlar görünsün"
    // So if a student has "12345", they should NOT appear.

    let hasValidPhone = isValidTurkishMobile;
    if (!hasValidPhone && phone.length > 11) {
      // Check if it contains a valid TR mobile number
      const matches = cleanPhone.match(/(05\d{9})/g);
      hasValidPhone = !!matches && matches.length > 0;
    }

    if (!hasValidPhone) return false;

    const query = searchQuery.toLowerCase();
    const matchesSearch = fullName.includes(query) || phone.includes(query);
    const matchesRegistrationYear =
      selectedRegistrationYears.length === 0 ||
      (student.type === 'lead' && selectedRegistrationYears.includes(String(student.registration_year || '')));

    if (!matchesSearch || !matchesRegistrationYear) return false;
    if (filterType === 'all') return true;
    if (filterType === 'consent') return student.marketing_consent === true;
    return student.type === filterType;
  });

  const allPhones = getAllPhones();
  const characterCount = message.length;
  const smsEstimate = estimateSms(message);
  const isGsm = smsEstimate.mode === 'GSM-7';
  const smsCount = calculateSmsCount(message);
  const studentCount = selectedStudents.filter(s => s.type === 'student').length;
  const leadCount = selectedStudents.filter(s => s.type === 'lead').length;
  const isVisaDept = (user?.unitType as string) === 'visa' || (user?.unitType as string) === 'visa_consultant';
  const currentYear = new Date().getFullYear();
  const registrationYearOptions = Array.from({ length: 8 }, (_, index) => String(currentYear - 2 + index));

  return (
    <div className="space-y-6 p-6">
      <div className="bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
              <MessageSquare className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-1">SMS Gönder</h1>
              <p className="text-blue-50">Öğrencilerinize toplu SMS gönderin</p>
            </div>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
            <div className="text-3xl font-bold">{allPhones.length}</div>
            <div className="text-sm text-blue-50">Toplam Alıcı</div>
          </div>
        </div>
      </div>

      <div role="tablist" aria-label="SMS işlemleri" className="flex gap-2 border-b border-gray-200 dark:border-gray-800">
        {([{ id: 'send', label: 'SMS Gönder' }, { id: 'history', label: 'Gönderim Kayıtları' }] as const).map(section => (
          <button key={section.id} id={`sms-tab-${section.id}`} role="tab" aria-selected={activeSection === section.id} aria-controls={`sms-panel-${section.id}`} onClick={() => setActiveSection(section.id)} className={`border-b-2 px-5 py-3 text-sm font-semibold transition-colors ${activeSection === section.id ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400'}`}>{section.label}</button>
        ))}
      </div>
      {activeSection === 'history' && <div role="tabpanel" id="sms-panel-history" aria-labelledby="sms-tab-history"><SmsHistory /></div>}
      <div role="tabpanel" id="sms-panel-send" aria-labelledby="sms-tab-send" hidden={activeSection !== 'send'} className="space-y-6">
      {showSuccess && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700 rounded-xl p-5 flex items-center space-x-4 shadow-lg animate-in slide-in-from-top">
          <div className="bg-green-500 p-2 rounded-full">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-semibold text-green-900 dark:text-green-300 text-lg">Başarılı!</p>
            <p className="text-green-700 dark:text-green-400">{successCount} alıcı için SMS siparişi kabul edildi. Teslimat sonuçlarını Gönderim Kayıtları sekmesinden takip edebilirsiniz.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span>Alıcı Seç</span>
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={selectAll}
                  className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  Görünenleri Seç
                </button>
                {selectedStudents.length > 0 && (
                  <>
                    <button
                      onClick={removeFilteredSelection}
                      className="px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                    >
                      Görünenleri Kaldır
                    </button>
                    <button
                      onClick={clearAll}
                      className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    >
                      Temizle
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="İsim, soyisim veya telefon ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/30 transition-all"
              />
            </div>

            <div className="flex items-center space-x-2 mb-4">
              <Filter className="w-4 h-4 text-gray-500" />
              <div className="flex space-x-2">
                <button
                  onClick={() => setFilterType('all')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${filterType === 'all'
                    ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                >
                  Tümü
                </button>
                {isVisaDept ? (
                  <>
                    <button
                      onClick={() => setFilterType('visa_tracking')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${filterType === 'visa_tracking'
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                    >
                      Vize (Takip)
                    </button>
                    <button
                      onClick={() => setFilterType('visa_appointment')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${filterType === 'visa_appointment'
                        ? 'bg-indigo-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                    >
                      Vize (Randevu)
                    </button>
                  </>
                ) : (user?.unitType as string) === 'testprep' ? (
                  // TestPrep users only see "All" which contains just students, so no extra buttons needed
                  null
                ) : (
                  <>
                    <button
                      onClick={() => setFilterType('student')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${filterType === 'student'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                    >
                      Öğrenci
                    </button>
                    <button
                      onClick={() => setFilterType('lead')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${filterType === 'lead'
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                    >
                      Lead
                    </button>
                    <button
                      onClick={() => setFilterType('consent')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${filterType === 'consent'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                    >
                      İletişim İzinli
                    </button>
                  </>
                )}
              </div>
            </div>

            {(user?.unitType as string) !== 'testprep' && !isVisaDept && (
              <div className="mb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Lead kayıt yılı:</span>
                  <button
                    type="button"
                    onClick={() => setSelectedRegistrationYears([])}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${selectedRegistrationYears.length === 0
                      ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                  >
                    Tüm yıllar
                  </button>
                  {registrationYearOptions.map(year => (
                    <button
                      key={year}
                      type="button"
                      onClick={() => toggleRegistrationYear(year)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${selectedRegistrationYears.includes(year)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Yükleniyor...</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {filteredStudents.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">Alıcı bulunamadı</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Farklı bir arama yapın</p>
                  </div>
                ) : (
                  filteredStudents.map(student => {
                    const isSelected = selectedStudents.find(s => s.id === student.id && s.type === student.type);
                    return (
                      <div
                        key={`${student.type}-${student.id}`}
                        onClick={() => toggleStudent(student)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all transform hover:scale-[1.02] ${isSelected
                          ? 'bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-400 dark:border-blue-600 shadow-md'
                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {student.first_name} {student.last_name}
                              </p>
                              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${student.type === 'lead'
                                ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800'
                                : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                                }`}>
                                {student.type === 'lead' ? 'Lead' : 'Öğrenci'}
                              </span>
                              {student.type === 'lead' && student.registration_year && (
                                <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                  {student.registration_year}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-1.5">
                              <Phone className="w-3.5 h-3.5" />
                              <span>{student.phone || 'Telefon yok'}</span>
                            </p>
                          </div>
                          {isSelected && (
                            <div className="bg-blue-500 p-1.5 rounded-full">
                              <CheckCircle className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/10 dark:to-amber-900/10 border-2 border-orange-200 dark:border-orange-800 rounded-2xl p-6 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <div className="bg-orange-100 dark:bg-orange-900/30 p-2 rounded-lg">
                <UserPlus className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <span>Manuel Telefon Ekle</span>
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 flex items-center space-x-1">
              <span>Numaraları</span>
              <span className="font-semibold">virgül (,)</span>
              <span>veya</span>
              <span className="font-semibold">alt satır</span>
              <span>ile ayırın</span>
            </p>
            <textarea
              value={manualPhones}
              onChange={(e) => setManualPhones(e.target.value)}
              placeholder="0530 123 4567&#10;0531 234 5678&#10;0532 345 6789"
              rows={5}
              className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none focus:border-orange-500 dark:focus:border-orange-400 focus:ring-2 focus:ring-orange-200 dark:focus:ring-orange-900/30 transition-all"
            />
            {manualPhones && (
              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-orange-200 dark:border-orange-800">
                <p className="text-sm font-semibold text-orange-700 dark:text-orange-300">
                  {parseManualPhones().length} telefon numarası eklendi
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <span>Seçili Alıcılar</span>
              </h2>
              <div className="flex items-center space-x-2">
                <div className="bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-lg border border-green-200 dark:border-green-800">
                  <span className="text-sm font-bold text-green-700 dark:text-green-300">{studentCount} Öğrenci</span>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 px-3 py-1 rounded-lg border border-orange-200 dark:border-orange-800">
                  <span className="text-sm font-bold text-orange-700 dark:text-orange-300">{leadCount} Lead</span>
                </div>
              </div>
            </div>

            {selectedStudents.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 font-medium">Henüz alıcı seçilmedi</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Sol taraftan alıcı seçin</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                {selectedStudents.map(student => (
                  <div
                    key={`${student.type}-${student.id}`}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="font-semibold text-gray-900 dark:text-white">{student.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${student.type === 'lead'
                          ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                          : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          }`}>
                          {student.type === 'lead' ? 'Lead' : 'Öğrenci'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{student.phone}</p>
                    </div>
                    <button
                      onClick={() => removeStudent(student.id, student.type)}
                      className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 border-2 border-purple-200 dark:border-purple-800 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <span>Mesajınız</span>
              </h2>
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                <span>Şablon</span>
              </button>
            </div>

            {showTemplates && (
              <div className="mb-4 space-y-3">
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAddTemplate(true)}
                    className="text-xs flex items-center space-x-1 text-purple-600 dark:text-purple-400 font-medium hover:underline"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Yeni Şablon Ekle</span>
                  </button>
                </div>

                {showAddTemplate && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800 space-y-3">
                    <input
                      type="text"
                      placeholder="Şablon Başlığı"
                      value={newTemplate.title}
                      onChange={e => setNewTemplate({ ...newTemplate, title: e.target.value })}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    />
                    <textarea
                      placeholder="Şablon İçeriği"
                      value={newTemplate.content}
                      onChange={e => setNewTemplate({ ...newTemplate, content: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white resize-none"
                    />
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setShowAddTemplate(false)}
                        className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        İptal
                      </button>
                      <button
                        onClick={handleAddTemplate}
                        className="px-3 py-1 text-xs font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700"
                      >
                        Kaydet
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                  {templates.length === 0 ? (
                    <div className="col-span-2 text-center text-gray-500 dark:text-gray-400 py-4 text-sm">
                      Henüz şablon bulunmuyor.
                    </div>
                  ) : (
                    templates.map(template => (
                      <div
                        key={template.id}
                        onClick={() => useTemplate(template)}
                        className="relative group text-left p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700 hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex justify-between items-start mb-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white pr-4">{template.title}</p>
                          {template.created_by === user?.id && (
                            <button
                              onClick={(e) => handleDeleteTemplate(e, template.id)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                              title="Şablonu Sil"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{template.content}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className={`cursor-pointer rounded-xl border-2 p-3 transition-colors ${messagePurpose === 'commercial'
                ? 'border-purple-500 bg-purple-100 dark:bg-purple-900/30'
                : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                }`}>
                <div className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="messagePurpose"
                    value="commercial"
                    checked={messagePurpose === 'commercial'}
                    onChange={() => setMessagePurpose('commercial')}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Pazarlama / Ticari</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Kampanya, tanıtım, teklif ve promosyon. İYS izni kontrol edilir.</p>
                  </div>
                </div>
              </label>

              <label className={`cursor-pointer rounded-xl border-2 p-3 transition-colors ${messagePurpose === 'informational'
                ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/30'
                : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                }`}>
                <div className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="messagePurpose"
                    value="informational"
                    checked={messagePurpose === 'informational'}
                    onChange={() => setMessagePurpose('informational')}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Bilgilendirme / İşlemsel</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Randevu, işlem, ödeme veya hesap bildirimi. Pazarlama içeriği için kullanmayın.</p>
                  </div>
                </div>
              </label>
            </div>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Mesajınızı buraya yazın...&#10;&#10;Örnek: Merhaba, yaklaşan randevunuz için sizi aramak istiyoruz."
              rows={7}
              className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none focus:border-purple-500 dark:focus:border-purple-400 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-900/30 transition-all"
            />
            <div className="flex items-center justify-between mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700">
              <div className="flex items-center space-x-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Karakter</p>
                  <p className={`text-sm font-bold ${characterCount > 160 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white'}`}>
                    {characterCount}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">SMS Sayısı</p>
                  <p className={`text-sm font-bold ${smsCount > 1 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white'}`}>
                    {smsCount}
                  </p>
                </div>
              </div>
              {characterCount > 160 && (
                <div className="flex items-center space-x-1 text-orange-600 dark:text-orange-400">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">Çoklu SMS</span>
                </div>
              )}
              {!isGsm && (
                <div className="flex items-center space-x-1 text-blue-600 dark:text-blue-400 ml-2">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">{smsEstimate.mode} • tahmini hesaplama</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl p-6 shadow-lg">
            <h3 className="font-bold text-lg text-blue-900 dark:text-blue-300 mb-4 flex items-center space-x-2">
              <div className="bg-blue-200 dark:bg-blue-800 p-1.5 rounded-lg">
                <CheckCircle className="w-4 h-4 text-blue-700 dark:text-blue-300" />
              </div>
              <span>Gönderim Özeti</span>
            </h3>
            <div className="mb-4 rounded-xl bg-white dark:bg-gray-800 p-4 border border-blue-200 dark:border-blue-700">
              <button type="button" onClick={checkBalance} disabled={checkingBalance || sending} className="text-sm font-semibold text-blue-700 dark:text-blue-300 disabled:opacity-50">{checkingBalance ? 'Bakiye sorgulanıyor…' : 'Canlı SMS bakiyesini kontrol et'}</button>
              <p className="text-xs text-gray-500 mt-1">SMS göndermez ve kredi harcamaz. Entegrasyonun bağlı olduğu hesabı sorgular.</p>
              {apiBalance && <div className="mt-3 text-sm text-gray-800 dark:text-gray-200"><p className="font-semibold">{apiBalance.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL {apiBalance.sms !== null && `• yaklaşık ${apiBalance.sms.toLocaleString('tr-TR')} standart SMS`}</p><p className="text-xs mt-1">Gönderici: {apiBalance.sender}. Bu tutarı İleti Merkezi panelinizdeki bakiye ile karşılaştırın.</p></div>}
              {balanceError && <p role="alert" className="text-sm text-red-600 mt-2">{balanceError}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Toplam Alıcı</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{allPhones.length}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Kişi Başı SMS</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{smsCount}</p>
              </div>
              <div className="col-span-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-4">
                <p className="text-xs text-blue-100 mb-1">Tahmini Toplam SMS Kredisi</p>
                <p className="text-3xl font-bold text-white">{allPhones.length * smsCount}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">Parça sayısı hesabın dil moduna göre değişebilir. Türkçe karakterler, emoji ve özel karakterler tüketimi etkiler; kesin ücret sağlayıcı tarafından belirlenir.</p>
          </div>

          {sendFailure && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">{sendFailure}</div>}

          <button
            onClick={handleSendSMS}
            disabled={sending || allPhones.length === 0 || !message.trim()}
            className={`w-full flex items-center justify-center space-x-3 py-5 rounded-2xl font-bold text-lg transition-all transform ${sending || allPhones.length === 0 || !message.trim()
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 hover:from-green-600 hover:via-emerald-600 hover:to-teal-600 text-white shadow-xl hover:shadow-2xl hover:scale-[1.02]'
              }`}
          >
            {sending ? (
              <>
                <Loader className="w-7 h-7 animate-spin" />
                <span>SMS Gönderiliyor...</span>
              </>
            ) : (
              <>
                <Send className="w-7 h-7" />
                <span>SMS Gönder ({allPhones.length} Alıcı)</span>
              </>
            )}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SmsSender;
